/* eslint-disable @typescript-eslint/no-magic-numbers */
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import SnackBar from '../components/SnackBar';

type SnackbarContextType = {
  showSnackbar: (
    message: string,
    variant?: string,
    onClose?: () => void,
    onClick?: () => void,
  ) => void;
};

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

export function SnackbarProvider({ children }: { children: ReactNode }) {
  const [snackbar, setSnackbar] = useState<{
    message: string;
    variant?: string;
    open: boolean;
    onClose?: () => void;
    onClick?: () => void;
  }>({
    message: '',
    open: false,
  });

  const showSnackbar = useCallback(
    (message: string, variant?: string, onClose?: () => void, onClick?: () => void) => {
      setSnackbar({
        message,
        variant,
        open: true,
        onClose,
        onClick: () => {
          setSnackbar({ message: '', open: false });
          if (onClick) onClick();
        },
      });
      setTimeout(() => {
        setSnackbar({ message: '', open: false });
        if (onClose) onClose();
      }, 5000);
    },
    [],
  );

  const value = useMemo(() => ({ showSnackbar }), [showSnackbar]);

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      {snackbar.open && snackbar.message && (
        <SnackBar
          text={snackbar.message}
          onClose={snackbar.onClose}
          onClick={snackbar.onClick}
          variant={snackbar.variant || 'error'}
        />
      )}
    </SnackbarContext.Provider>
  );
}
